import { useCallback, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getName } from "Utils/Item";

import { projectsCloneStart, projectsCloneDialog } from "../../../Store/Actions";
import { selectCloneIndexRaw, selectCloningProject, selectShowCloneDialog } from "../../../Store/Selectors/Clone";
import { selectSelectedOrActiveProjectIds, selectSelectedProjects } from "../../../Store/Selectors/Projects";
import { useAuthenticationPreflight } from "../../Generic/Authentication";
import { BaseDialog } from "../../Generic/BaseDialog";
import { IWithTranslatorProps, WithTranslator } from "../../Generic/Translator";
import { ConfirmDialog } from "../ConfirmDialog";

export const Clone = WithTranslator((props: IWithTranslatorProps): JSX.Element => {
  const { translator } = props;
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmHidden, setIsConfirmHidden] = useState(true);
  const [isCloning, setIsCloning] = useState(false);
  const { preflightAuthentication } = useAuthenticationPreflight();

  const onDismiss = useCallback(() => {
    dispatch(projectsCloneDialog(false));
  }, [dispatch]);

  const onConfirm = useCallback(async () => {
    setIsConfirmHidden(true);
    setIsOpen(true);

    await preflightAuthentication();
    dispatch(projectsCloneStart());
  }, [dispatch, preflightAuthentication]);

  const onConfirmDismiss = useCallback(() => {
    setIsConfirmHidden(true);
    setIsOpen(false);
    dispatch(projectsCloneDialog(false));
  }, [dispatch]);

  const selectedProjects = useSelector(selectSelectedProjects);
  const showCloneDialog = useSelector(selectShowCloneDialog);
  const cloningProject = useSelector(selectCloningProject);
  const totalCloneCount = useSelector(selectSelectedOrActiveProjectIds).length;
  const rawCloneIndex = useSelector(selectCloneIndexRaw);

  useLayoutEffect(() => {
    if (!showCloneDialog) {
      setTimeout(() => {
        setIsConfirmHidden(true);
        setIsOpen(false);
      }, 250);
      return;
    }

    if (!isCloning) {
      setIsConfirmHidden(false);
    }
  }, [dispatch, isCloning, onConfirm, preflightAuthentication, selectedProjects, showCloneDialog, totalCloneCount]);

  useLayoutEffect(() => {
    setIsCloning(cloningProject != null && rawCloneIndex >= 0);
  }, [rawCloneIndex, cloningProject]);

  const title = cloningProject != null
    ? `${translator.get(`clone`, `Cloning project`)}... - ${getName(cloningProject)}`
    : `${translator.get(`generic.done`, `Done`)}...`;

  return (
    <>
      <BaseDialog isOpen={isOpen} title={title} showCloseButton onDismiss={onDismiss}>
        <span>
          {cloningProject != null
            && translator.get(`clone.progress`, `Cloning project. The dialog will close when done.`)}
        </span>
      </BaseDialog>
      <ConfirmDialog
        confirmButtonText={translator.get(`clone.confirm.yes`, `Yes`)}
        dismissButtonText={translator.get(`clone.confirm.no`, `No, please don't`)}
        hidden={isConfirmHidden}
        onConfirm={onConfirm}
        onDismiss={onConfirmDismiss}
        subText={translator.get(`clone.confirm.text`, `Are you sure you want to clone the project?`)}
        title={translator.get(`clone.confirm.title`, `Clone project!`)}
      />
    </>
  );
});
